import React from "react";
import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { TableSortIndicator } from "../../helper/tableSortIndicator";
import { contactChannelForColumn, contactRunForColumn } from '../../helper/leadContactStatus';
import LeadContactValues from '../../components/Common/LeadContactValues';
import {
  GENERATED_CELL_STATUS,
  formatGeneratedCellError,
  normalizeGeneratedCellStatus,
} from "../../helper/generatedColumn";

/**
 * Skeleton placeholder for a generated-column cell while results load.
 * `variant`: "short" (email/score) | "long" (AI prose).
 */
export function GeneratedColumnSkeletonCell({
  variant = "short",
  "data-testid": testId = "generated-column-skeleton",
}) {
  const isLong = variant === "long";
  const shimmerSx = {
    borderRadius: "4px",
    "@keyframes generatedColumnShimmer": {
      "0%": { backgroundPosition: "100% 0" },
      "100%": { backgroundPosition: "-100% 0" },
    },
    backgroundImage: (theme) =>
      theme.palette.mode === "dark"
        ? "linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.06) 100%)"
        : "linear-gradient(90deg, rgba(88,89,91,0.08) 0%, rgba(88,89,91,0.16) 50%, rgba(88,89,91,0.08) 100%)",
    backgroundSize: "200% 100%",
    animation: "generatedColumnShimmer 1.4s ease-in-out infinite",
  };

  return (
    <Box
      data-testid={testId}
      sx={{
        px: 1,
        py: isLong ? 1.25 : 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 0.6,
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {isLong ? (
        <>
          <Skeleton
            animation={false}
            variant="rounded"
            height={12}
            width="92%"
            sx={shimmerSx}
          />
          <Skeleton
            animation={false}
            variant="rounded"
            height={12}
            width="78%"
            sx={shimmerSx}
          />
          <Skeleton
            animation={false}
            variant="rounded"
            height={12}
            width="64%"
            sx={shimmerSx}
          />
        </>
      ) : (
        <Skeleton
          animation={false}
          variant="rounded"
          height={14}
          width="72%"
          sx={{ ...shimmerSx, maxWidth: 140 }}
        />
      )}
    </Box>
  );
}

/**
 * Header for a generated column — optional compact loading indicator while processing.
 */
export function GeneratedColumnHeaderCell({
  name,
  sortDirection,
  isProcessing = false,
  sortable = false,
}) {
  const title = isProcessing ? `${name} · Generating…` : name;

  return (
    <Box
      component="span"
      sx={{
        alignItems: "center",
        display: "flex",
        inset: 0,
        minWidth: 0,
        px: 1,
        position: "absolute",
      }}
    >
      <Box
        component="span"
        sx={{
          alignItems: "center",
          display: "inline-flex",
          gap: 0.5,
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <Typography
          component="span"
          title={String(title || "")}
          sx={{
            color: "inherit",
            flex: "0 1 auto",
            fontSize: "12px",
            fontWeight: 800,
            lineHeight: 1.2,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {isProcessing ? (
          <CircularProgress
            size={12}
            thickness={5}
            color="primary"
            data-testid="generated-column-header-loading"
            sx={{ flex: "0 0 auto" }}
          />
        ) : null}
        {sortable ? <TableSortIndicator sortDirection={sortDirection} /> : null}
      </Box>
    </Box>
  );
}

/**
 * Renders a generated-column cell from normalized status.
 * Non-targeted rows (no entry) stay inactive with an em dash.
 */
export function GeneratedColumnResultCell({
  actionKey,
  entry,
  leadContacts = [],
  isLongForm = false,
  formatVerifyEmailCellError,
  theme,
}) {
  const status = normalizeGeneratedCellStatus(entry?.status);

  if (!entry || !status) {
    return (
      <Box
        sx={{
          px: 1,
          height: "100%",
          display: "flex",
          alignItems: "center",
          color: "text.disabled",
        }}
      >
        —
      </Box>
    );
  }

  if (status === GENERATED_CELL_STATUS.QUEUED || status === GENERATED_CELL_STATUS.LOADING) {
    return (
      <GeneratedColumnSkeletonCell variant={isLongForm ? "long" : "short"} />
    );
  }

  if (status === GENERATED_CELL_STATUS.ERROR) {
    const displayError = formatGeneratedCellError(actionKey, entry.error, {
      formatVerifyEmailCellError,
    });
    return (
      <Box
        sx={{
          px: 1,
          display: "flex",
          alignItems: isLongForm ? "flex-start" : "center",
          pt: isLongForm ? 1.5 : 0,
          gap: 1,
          height: "100%",
        }}
      >
        <ErrorOutlineIcon
          sx={{
            fontSize: 16,
            color: theme?.palette?.error?.main || "error.main",
            mt: isLongForm ? 0.25 : 0,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {displayError}
        </Typography>
      </Box>
    );
  }

  const content = actionKey === "verify_email" ? `Score: ${entry.result}%` : entry.result;
  const channel = contactChannelForColumn(actionKey);
  if (channel && leadContacts.length) {
    return <Box tabIndex={0} role="region" aria-label="Lead contact values"
      onKeyDown={(event) => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)) event.stopPropagation();
      }}
      sx={{px: 1, py: 0.5, minWidth: 0, height: '100%', boxSizing: 'border-box', overflowY: 'auto', overflowX: 'hidden'}}>
      <LeadContactValues value={content} contacts={leadContacts} channel={channel} run={contactRunForColumn(actionKey)} />
    </Box>;
  }

  return (
    <Box
      sx={{
        px: 1,
        py: isLongForm ? 1.5 : 0,
        display: "flex",
        alignItems: isLongForm ? "flex-start" : "center",
        gap: 1,
        minWidth: 0,
        height: "100%",
      }}
      title={isLongForm ? undefined : String(content || "")}
    >
      <CheckCircleIcon
        sx={{
          fontSize: 16,
          color: theme?.palette?.success?.main || "success.main",
          flex: "0 0 auto",
          mt: isLongForm ? 0.25 : 0,
        }}
      />
      <Typography
        variant="body2"
        sx={
          isLongForm
            ? {
                display: "-webkit-box",
                WebkitLineClamp: 6,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                whiteSpace: "normal",
                lineHeight: 1.4,
                fontSize: "13px",
              }
            : {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "13px",
              }
        }
      >
        {content}
      </Typography>
    </Box>
  );
}
